import React from 'react'
import { notEmpty } from 'helpers/validators'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import pick from 'lodash/pick'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Form, Checkbox, Number as NumberInput, Select } from 'components/forms'
import ContentHeader from 'components/ContenHeader'
import { useQuery } from 'components/graphql'
import { ConfirmModal } from 'components/Modal'
import { listProducts } from 'sections/product/gql/query'
import { getClientIdentity } from 'gql/query'
import loadAppConstants from 'gql/query/loadAppConstants'
import { FormCol, FormRow } from 'style/styled'

import { CashFlow, General, QRCode, Pms } from './Blocks/'
import listRestaurants from '../../gql/listRestaurants'

export const RoomSelector = ({ designProjectId, field, label, isMulti, ...rest }) => {
	const [t] = useTranslation()
	return (
		<Select
			field={field}
			label={label}
			isMulti={isMulti}
			beforeSetFormValue={v => isMulti ? v.map(({ value }) => value) : v}
			remoteOptions={{
				query: listRestaurants,
				modifyOptions: (item) => {
					const pos = item.filter(({ id }) => id === designProjectId)
					return pos.reduce((prevRest, currentRest) => {
						const floors = currentRest.floors.reduce((prevFloor, currentFloor) => {
							const rooms = currentFloor.rooms.reduce((prevRoom, currentRoom) => {
								const res = {
									value: currentRoom?.id,
									label: `${t('options_section.fields.raum', { room_val: currentRoom?.caption })}, ${t('options_section.fields.stockwerk', { floor_val: currentFloor?.number })}`
								}
								return [...prevRoom, res]
							}, [])
							return [...prevFloor, ...rooms]
						}, [])
						return [...prevRest, ...floors]
					}, [])
				}
			}}
			{...rest}
		/>
	)
}


const Kasse = ({ formData, data, itemId, mutations: { updateItem } }) => {
	const { designProject } = formData.parameters || {}
	const [t, i18n] = useTranslation()
	const getFormData = fields => pick(formData, fields)
	const { data: companyIdData } = useQuery(getClientIdentity, {
		fetchPolicy: 'cache-first'
	})
	const companyId = companyIdData?.whoami?.companyId?.toUpperCase?.()?.replace?.(/_/g, '-')
	const isMobileWaiter = data?.deviceType === 'MOBILE_WAITER'

	return(
		<ExpandableBlocks>
			<ContentBlock label={t('options_section.strings.general')}>
				<General initialValues={formData} onSubmit={updateItem} />
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.cash_flow')}>
				<CashFlow data={data} formData={formData} updateItem={updateItem} itemId={itemId} />
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.usage')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={updateItem}
				>
					{({ formApi, formState }) => (
						<>
							<ContentHeader title={t('options_section.strings.restaurant_design')} />
							<Select
								field={'parameters.designProject'}
								label={t('options_section.fields.location')}
								remoteOptions={{ query: listRestaurants, valueField: 'id', labelField: 'caption' }}
								valueField='id'
								labelField='caption'
								isClearable
								beforeSetFormValue={async value => {
									let newValue = formData.parameters?.designProject
									try {
										await ConfirmModal({
											text: t('options_section.strings.kitchen_printer_restaurant_warning'),
											btnText: t('common.buttons.confirm')
										})
										newValue = value
									} catch (e) {
										return formData.parameters?.designProject
									}
									return newValue
								}}
							/>
							<Checkbox
								field='parameters.restaurantIsMainMode'
								label={t('options_section.fields.restaurant_main_mode')}
							/>
							<Checkbox
								field='parameters.restaurantBirdViewActive'
								label={t('options_section.fields.restaurant_bird_view')}
							/>
							<Checkbox
								field='parameters.isHospitalityReceiptEnabled'
								label={t('options_section.fields.is_hospitality_bill_active')}
							/>
							<Checkbox
								field='parameters.isInCafeConfirmationBeforePayment'
								label={t('options_section.fields.isincafe_confirmation_before_payment')}
							/>
							{isMobileWaiter &&
								<Checkbox
									field='parameters.retrieveNumberOfGuests'
									label={t('options_section.fields.retrieve_number_of_guests')}
								/>
							}
							<RoomSelector
								field='parameters.restaurantDefaultRoom'
								label={t('options_section.strings.preset_room')}
								designProjectId={designProject}
							/>
							<ExpandableBlocks nested>
								{(companyId === '9712049C-1325-430F-B0C8-F6D64E5CF9C5' || companyId === '57CCBFB2-724D-F9CA-5202-ADD5FB4678F9') &&
									<ContentBlock label={t('options_section.strings.qr_code')} unmountOnCollapse>
										<QRCode
											cashRegisterId={itemId}
											companyId={companyId}
										/>
									</ContentBlock>
								}
								<ContentBlock label={t('options_section.strings.miscellaneous')}>
									<FormRow>
										<FormCol xs>
											<Select
												style={{ minWidth: '210px' }}
												label={t('options_section.fields.gastro_orders')}
												field='parameters.serviceCallMode'
												options={[
													{ value: 0, label: t('options_section.strings.not_active') },
													{ value: 1, label: t('options_section.strings.gastro_order_article_related') },
													{ value: 2, label: t('options_section.strings.gastro_order_receipt_related') }
												]}
											/>
										</FormCol>
									</FormRow>
								</ContentBlock>
								<ContentBlock label={t('options_section.strings.clickcollect')}>
									<Select
										label={t('options_section.fields.preoderPrepaidProduct')}
										field='parameters.preoderPrepaidProduct'
										style={{ width: '100%' }}
										valueField='id'
										labelField='name'
										withVirtualBody={true}
										isSearchable={true}
										remoteOptions={{
											query: listProducts,
											variables: {
												filter: { isPreorderProduct: true }
											}
										}}
									/>
									<Select
										label={t('options_section.fields.preoderPickupProduct')}
										field='parameters.preoderPickupProduct'
										style={{ width: '100%' }}
										valueField='id'
										labelField='name'
										withVirtualBody={true}
										isSearchable={true}
										remoteOptions={{
											query: listProducts,
											variables: {
												filter: { isPreorderProduct: true }
											}
										}}
									/>


								</ContentBlock>
							</ExpandableBlocks>
						</>
					)}
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.closing')}>
				<Form
					initialValues={formData}
					onSubmit={updateItem}
					smartSubmit
				>
					<Select
						field='parameters.closingType'
						label={t('options_section.fields.closing_type')}
						remoteOptions={{
							query: loadAppConstants,
							variables: { type: 75, language: i18n.language },
							modifyOptions: opts => opts.reduce((outArr, { value, label }) => {
								if(label && (value === '10' || value === '110' || value === '8' || value === '12' || value === '16')) { // Only four options are used for now
									let enumVal = null
									const parsedLabel = label.indexOf('CH - ') === 0 ? label.substring(5) : label
									switch(value) {
										case '10': enumVal = 'standard'
											break
										case '110': enumVal = 'standardWithoutPrinting'
											break
										case '16': enumVal = 'withCashReportAndCountProtocol'
											break
										case '8': enumVal = 'withCashReport'
											break
										case '12': enumVal = 'withoutCash'
											break
										default:
									}
									outArr.push({ value: enumVal, label: parsedLabel })
								}
								return outArr
							}, [])
						}}
						isClearable
					/>
					<Checkbox
						field='parameters.isEmployeeClosingActive'
						label={t('options_section.fields.is_empolyee_closing')}
					/>
					<Checkbox
						field='parameters.correctionTo'
						label={t('options_section.fields.correctionTo')}
					/>
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.security')}>
				<Form
					smartSubmit
					initialValues={formData} // Informed v3 adds to the formState only fields that have corresponding nodes
					onSubmit={updateItem}
				>
					<Checkbox field='parameters.drawerCloseCheck' label={t('options_section.fields.compulsory_closure')} />
					<Checkbox field='parameters.hideOpenDrawerBtn' label={t('options_section.fields.hide_open_button')} />
					<Checkbox field='parameters.isDrawerOpenDuringClosing' label={t('options_section.fields.open_drawer_closing')} />
					<Checkbox field='parameters.isReturnButtonDeleted' label={t('options_section.fields.delete_return_button')} />
				</Form>
			</ContentBlock>

			<ContentBlock label={t('common.strings.vat')}>
				<Form
					smartSubmit
					initialValues={getFormData(['parameters.normalVat', 'parameters.reducedVat', 'parameters.isInCafeModeInt', 'parameters.specialVat'])}
					onSubmit={updateItem}
				>
					<Row>
						<Col xs>
							<NumberInput
								field='parameters.normalVat'
								precision={1}
								withControls={false}
								label={t('options_section.fields.normal_vat')}
								{...notEmpty()}
							/>
							<NumberInput
								field='parameters.reducedVat'
								precision={1}
								withControls={false}
								label={t('options_section.fields.reduced_vat')}
								{...notEmpty()}
							/>
							<NumberInput
								field='parameters.specialVat'
								precision={1}
								withControls={false}
								label={t('options_section.fields.special_vat')}
								{...notEmpty()}
							/>
						</Col>
						<Col xs>
							<Select
								field='parameters.isInCafeModeInt'
								label={t('options_section.fields.inCafe')}
								options={[
									{ value: 0, label: t('options_section.fields.tax_reduced') },
									{ value: 1, label: t('options_section.fields.tax_select') },
									{ value: 2, label: t('options_section.fields.tax_normal') }
								]}
							/>
						</Col>
					</Row>
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.time_tracking')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={updateItem}
				>
					<Checkbox field='parameters.isActiveTimeAssist' label={t('options_section.fields.activate_timeassist')} />
					<Checkbox field='parameters.loginOnlyAfterTimeRecording' label={t('options_section.fields.login_after_recording')} />
					<Checkbox field='parameters.logoutOnlyAfterTimeRecording' label={t('options_section.fields.logout_after_recording')} />
					<Checkbox field='parameters.isSellProductInWorkingTime' label={t('options_section.fields.register_products_logged_user')} />
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.pms')}>
				<Pms
					formData={formData}
					updateItem={updateItem}
				/>
			</ContentBlock>

		</ExpandableBlocks>
	)
}

export default Kasse
